import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { BillerCategory, IBiller } from '../models/model';
import Fuse from 'fuse.js';

const fuseOptions: Fuse.IFuseOptions<IBiller> = {
  includeScore: true,
  keys: ['name', 'aliases'],
  location: 0,
  minMatchCharLength: 2,
  threshold: 0.4,
};

const rankWeight = 0.2;

@Injectable({
  providedIn: 'root',
})
export class BillerSearchService {
  public fuse: Fuse<IBiller> | null = null;

  public loading: boolean;
  public minRank: number;
  public maxRank: number;
  private billers: IBiller[];

  constructor(private api: ApiService) {
  }

  private static matchesCategory(biller: IBiller, category: BillerCategory): boolean {
    return !biller.categories || biller.categories.includes(category);
  }

  public init(): Observable<IBiller[]> {
    this.loading = true;
    return this.api.get<IBiller[]>('/billers').pipe(
      tap((billers: IBiller[]) => {
        billers.forEach(biller => biller.initial = biller.name.substring(0, 1));
        this.billers = billers.sort((a, b) => a.name.localeCompare(b.name));
        const sortedByRank = [...this.billers];
        sortedByRank.sort((a, b) => (b.rank || 0) - (a.rank || 0));
        this.maxRank = sortedByRank[0].rank || 0;
        this.minRank = sortedByRank[sortedByRank.length - 1].rank || 0;
        this.fuse = new Fuse(this.billers, fuseOptions);
        this.loading = false;
      }),
    );
  }

  public initEbokBillers(): Observable<IBiller[]> {
    this.loading = true;
    return this.api.get<IBiller[]>('/billers/ebok').pipe(
      tap((billers: IBiller[]) => {
        billers.forEach(biller => biller.initial = biller.name.substring(0, 1));
        this.billers = billers.sort((a, b) => a.name.localeCompare(b.name));
        const sortedByRank = [...this.billers];
        sortedByRank.sort((a, b) => (b.rank || 0) - (a.rank || 0));
        this.maxRank = sortedByRank[0].rank || 0;
        this.minRank = sortedByRank[sortedByRank.length - 1].rank || 0;
        this.fuse = new Fuse(this.billers, fuseOptions);
        this.loading = false;
      }),
    );
  }

  public search(query: string, category?: BillerCategory): ISearchResult {
    let billers = this.searchBillers(query);
    if (category) {
      billers = billers.filter((biller) => BillerSearchService.matchesCategory(biller, category));
    }
    if (query) {
      return {
        all: billers,
        popular: [],
      };
    } else {
      const popular = [...billers].sort((a, b) => (b.rank || 0) - (a.rank || 0)).slice(0, 10);
      if (category) {
        popular.forEach((p) => billers.splice(billers.indexOf(p), 1));
      }
      return { popular, all: billers };
    }
  }

  private searchBillers(query: string): IBiller[] {
    query = (query || '').trim();
    if (!query || !this.fuse) {
      return this.billers;
    }

    const result = this.fuse.search<IBiller>(query);

    result.forEach(
      (r) =>
        (r.score =
          (1 - rankWeight) * r.score + rankWeight * (1 - (r.item.rank - this.minRank) / (this.maxRank - this.minRank))),
    );

    result.sort((a, b) => a.score - b.score);

    return result.map((b) => (b as any).item);
  }
}

interface ISearchResult {
  popular: IBiller[];
  all: IBiller[];
}
